import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetIconSx,
  DASHBOARD_MID_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState, type ElementType } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { useGetCustomerBreakdownService } from "subModule/src/services/analytics/useAnalytics";
import { GetCustomerBreakdownResponse } from "subModule/src/services/analytics/interface";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface CustomerBreakdownProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function CustomerBreakdown(props: CustomerBreakdownProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetCustomerBreakdownService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 10);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetCustomerBreakdownResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  return (
    <Card
      sx={{
        height: DASHBOARD_MID_WIDGET_HEIGHT,
        minHeight: DASHBOARD_MID_WIDGET_HEIGHT,
        boxShadow: "none",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "12px 16px !important",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, flexShrink: 0 }}>
          <AnalyticsWidgetHeader
            title={t("CustomerBreakdown")}
            icon={<GroupsOutlinedIcon />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "customerBreakdown"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "customerBreakdown",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "customerBreakdown",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions(
                          "customerBreakdown",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </>
        ) : (
          <Box
            className="grid gap-2"
            sx={{
              mt: 1,
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              alignContent: "stretch",
            }}
          >
            {(
              [
                {
                  key: "newCustomers",
                  labelKey: "newCustomers",
                  value: Number(
                    typeof data?.newCustomers === "object"
                      ? data?.newCustomers?.value
                      : data?.newCustomers || 0,
                  ),
                  color: props.themes?.primary || "#7C3AED",
                  Icon: PersonAddAltOutlinedIcon,
                },
                {
                  key: "returnedCustomers",
                  labelKey: "returnedCustomers",
                  value: Number(
                    typeof data?.returnedCustomers === "object"
                      ? data?.returnedCustomers?.value
                      : data?.returnedCustomers || 0,
                  ),
                  color: props.themes?.secondary || "#EA580C",
                  Icon: PersonOutlineOutlinedIcon,
                },
                {
                  key: "servedCustomers",
                  labelKey: "servedCustomers",
                  value: Number(data?.servedCustomers || 0),
                  color: "#1CACDC",
                  Icon: GroupsOutlinedIcon,
                },
                {
                  key: "totalInvoices",
                  labelKey: "totalInvoices",
                  value: Number((data?.open || 0) + (data?.totalInvoices || 0)),
                  color: "#EF212C",
                  Icon: ReceiptLongOutlinedIcon,
                },
              ] as {
                key: string;
                labelKey: string;
                value: number;
                color: string;
                Icon: ElementType;
              }[]
            ).map((metric) => {
              const Icon = metric.Icon;
              return (
                <Box
                  key={metric.key}
                  className="flex items-center justify-between gap-1.5 rounded-[10px]"
                  sx={{
                    backgroundColor: `${metric.color}12`,
                    px: { xs: 1, sm: 1.25 },
                    py: { xs: 0.75, sm: 1 },
                    minWidth: 0,
                    minHeight: 0,
                  }}
                >
                  <Box className="flex items-center gap-1.5 min-w-0">
                    <Box
                      className="flex flex-col gap-0.5 min-w-0"
                      sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
                    >
                      <Box
                        sx={{
                          ...dashboardWidgetIconSx,
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          backgroundColor: metric.color,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 14, color: "#FFFFFF" }} />
                      </Box>
                      <Typography
                        className="font-Saira"
                        sx={{
                          fontSize: { xs: 10, sm: 11 },
                          fontWeight: 600,
                          color: "#6B7280",
                          lineHeight: 1.25,
                        }}
                      >
                        {t(metric.labelKey)}
                      </Typography>
                    </Box>
                  </Box>

                  <Tooltip
                    placement="top"
                    title={metric.value.toLocaleString()}
                    enterDelay={0}
                  >
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: { xs: 18, sm: 24 },
                        fontWeight: 800,
                        color: metric.color,
                        lineHeight: 1.1,
                        flexShrink: 0,
                      }}
                    >
                      {new Intl.NumberFormat("en", {
                        notation: "compact",
                        compactDisplay: "short",
                        maximumFractionDigits: 2,
                      }).format(metric.value)}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
